import { inject as service } from '@ember/service';
import { empty, filterBy, notEmpty, readOnly, sort } from '@ember/object/computed';
import Controller from '@ember/controller';
import { action, computed, get } from '@ember/object';
import { isBlank, isEmpty } from '@ember/utils';
import paged from 'cio/utils/paged';
import searchByProperty from 'cio/utils/search-by-property';
import { task } from 'ember-concurrency';
import SegmentModel, { SegmentType, SEGMENT_TYPES } from 'cio/models/segment';
import AlertsService from 'cio/services/alerts';
import SegmentActionsService from 'cio/services/segment-actions';
import AudienceModel from 'cio/models/audience';
import TagModel from 'cio/models/tag';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { tracked } from '@glimmer/tracking';
import ObjectTypesService from 'cio/services/object-types';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';

const USAGE_TYPES = {
  default: 'all',
  in_use: 'in_use',
  not_in_use: 'not_in_use',
  in_sent_newsletters: 'in_sent_newsletters',
};

export const TYPE_FILTERS = [
  { label: 'All Types', value: SEGMENT_TYPES.default },
  { label: 'Manual Segments', value: SEGMENT_TYPES.manual },
  { label: 'Data-driven Segments', value: SEGMENT_TYPES.dynamic },
];

export const USAGE_FILTERS = [
  { label: 'All Usage', value: USAGE_TYPES.default },
  { label: 'In Use', value: USAGE_TYPES.in_use },
  { label: 'Unused', value: USAGE_TYPES.not_in_use },
  { label: 'Only in sent newsletters', value: USAGE_TYPES.in_sent_newsletters },
];

export default class SegmentsIndexController extends Controller {
  queryParams = [
    'search',
    'searchTagIds',
    'page',
    'type',
    'archived',
    'usage',
    'audienceStatuses',
    'audienceTypes',
    'objectTypeIds',
  ];

  @service alerts!: AlertsService;
  @service segmentActions!: SegmentActionsService;
  @service objectTypes!: ObjectTypesService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;

  @tracked search = '';
  @tracked searchTagIds: string[] = [];
  @tracked page = 1;
  @tracked archived = false;
  @tracked type = SEGMENT_TYPES.default;
  @tracked usage = USAGE_TYPES.default;
  @tracked audienceStatuses: string[] = [];
  @tracked audienceTypes: string[] = [];
  @tracked objectTypeIds: string[] = [];

  itemsPerPage = 20;
  _typeFilters = TYPE_FILTERS;
  _didSelectAll = false;
  _selectedSegments: SegmentModel[] = [];

  @computed('archived')
  get _usageFilters() {
    if (this.archived) {
      return USAGE_FILTERS.filter((filter) => filter.value !== USAGE_TYPES.in_use);
    } else {
      return USAGE_FILTERS;
    }
  }

  @readOnly('model.segments') segments!: SegmentModel[];
  @empty('segments') noSegments!: boolean;
  @readOnly('model.audiences') audiences!: AudienceModel[];
  @notEmpty('audiences') hasAudiences!: boolean;

  sortProperty = ['updatedAt:desc'];

  @sort('segments.[]', 'sortProperty') sortedSegments!: SegmentModel[];

  @filterBy('segments', 'archived', false) activeSegments!: SegmentModel[];
  @filterBy('segments', 'archived', true) archivedSegments!: SegmentModel[];

  @computed(
    'sortedSegments',
    'search',
    'searchTagIds.[]',
    'type',
    'archived',
    'usage',
    'audienceStatuses',
    'audienceTypes',
    'objectTypeIds',
  )
  get filteredSegments() {
    let { search, searchTagIds, type, sortedSegments, archived, usage, objectTypeIds } = this;
    let segments = sortedSegments;

    if (type !== SEGMENT_TYPES.default) {
      segments = segments.filterBy('type', type as SegmentType);
    }

    if (usage === USAGE_TYPES.in_use) {
      segments = segments.filterBy('inUseActive', true);
    } else if (usage === USAGE_TYPES.not_in_use) {
      segments = segments.filterBy('inUseActive', false);
    } else if (usage === USAGE_TYPES.in_sent_newsletters) {
      segments = segments.filterBy('usedInOnlySentNewsletters', true);
    }

    if (!isBlank(search)) {
      segments = searchByProperty(segments, ['name', 'description'], search);
    }

    if (!isBlank(searchTagIds)) {
      segments = segments.filter((segment) => {
        let segmentTagIds = segment.get('tags').map((tag) => tag.id);
        return searchTagIds.every((id) => segmentTagIds.includes(id));
      });
    }

    if (!isBlank(objectTypeIds)) {
      segments = segments.filter((segment) => {
        let segmentObjectIds = segment.get('objectTypes').map((objectType) => objectType.id);
        return objectTypeIds.every((id) => segmentObjectIds.includes(id));
      });
    }

    let { audienceStatuses, audienceTypes } = this;

    if (!isEmpty(audienceStatuses)) {
      segments = segments.filter((segment) => {
        return (segment.audiences.content as AudienceModel[])
          .compact()
          .some((audience) => audienceStatuses.includes(audience?.status?.id));
      });
    }

    if (!isEmpty(audienceTypes)) {
      segments = segments.filter((segment) => {
        return (segment.audiences.content as AudienceModel[])
          .compact()
          .some((audience) => audienceTypes.includes(get(audience, 'type')));
      });
    }

    return segments.filterBy('archived', archived);
  }

  @paged('filteredSegments') pagedSegments!: SegmentModel[];

  @computed('_selectedSegments.[]', 'pagedSegments.[]')
  get _allSelected() {
    return this.pagedSegments.every((segment) => this._selectedSegments.includes(segment));
  }

  @computed('type')
  get emptyStateMessage() {
    if (this.type === SEGMENT_TYPES.manual) {
      return 'No manual segments found';
    }

    if (this.type === SEGMENT_TYPES.dynamic) {
      return 'No data-driven segments found';
    }

    return 'No segments found';
  }

  _archive = task({ drop: true }, async (archive: boolean) => {
    return this._action.perform(archive ? 'archive' : 'unarchive');
  });

  _delete = task({ drop: true }, async () => {
    return this._action.perform('delete');
  });

  _action = task({ drop: true }, async (action: 'archive' | 'unarchive' | 'delete') => {
    await this.segmentActions[action](this._selectedSegments.mapBy('id'));

    this.resetSelectedSegments();
    this.send('refreshModel');
  });

  _confirmConsent = task({ drop: true }, async () => {
    let audienceIdList: string[] = [];
    this._selectedSegments.forEach((s) => {
      s.audiences.forEach((a) => {
        audienceIdList.push(a.id);
      });
    });
    try {
      await this.api.request('audiences/bulk_consent', {
        method: 'POST',
        pathScope: 'environment',
        data: { audience_ids: audienceIdList },
      });
      this.alerts.success('audience.bulk.consent.success', 'Consent confirmed, audiences will update shortly');
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  get canConfirmConsent() {
    return this.audienceTypes.length === 1 && this.audienceTypes[0] === 'adwords';
  }

  setDefaults() {
    this.setProperties({
      page: 1,
      search: '',
      searchTagIds: [],
      type: SEGMENT_TYPES.default,
      usage: USAGE_TYPES.default,
    });
  }

  resetPagination() {
    this.set('page', 1);
  }

  resetSelectedSegments() {
    this.set('_selectedSegments', []);
  }

  @action
  didSearch(evt: FlyKeyboardEvent<HTMLInputElement>) {
    this.search = evt.target.value;
    this.resetPagination();
    this.resetSelectedSegments();
  }

  @action
  onSelect(segment: SegmentModel) {
    if (this._selectedSegments.includes(segment)) {
      this._selectedSegments.removeObject(segment);
    } else {
      this._selectedSegments.pushObject(segment);
    }
  }

  @action
  onSelectAll(select: boolean) {
    if (select) {
      this.set('_selectedSegments', [...this.pagedSegments]);
      this.set('_didSelectAll', true);
    } else {
      this.resetSelectedSegments();
      this.set('_didSelectAll', false);
    }
  }

  @action
  onTagClicked(tag: TagModel) {
    this.set('searchTagIds', [tag.id]);
  }

  @action
  onFilterChange(filter: keyof SegmentsIndexController, value: unknown) {
    this.set(filter, value);
    this.resetSelectedSegments();
    this.resetPagination();
  }

  @action
  toggleArchived(archived: boolean) {
    if (this.archived === archived) {
      return;
    }

    this.set('archived', archived);
    this.setDefaults();
    this.resetSelectedSegments();
  }

  @action
  setSearchTagIds(tagIds: string[]) {
    this.searchTagIds = tagIds;
    this.resetPagination();
  }

  @action
  setAudienceStatuses(statuses: string[]) {
    this.audienceStatuses = statuses;
    this.resetPagination();
  }

  @action
  setAudienceTypes(types: string[]) {
    this.audienceTypes = types;
    this.resetPagination();
  }

  @action
  setObjectTypeIds(objectTypeIds: string[]) {
    this.objectTypeIds = objectTypeIds;
    this.resetPagination();
  }
}
